import React from 'react';
import styled from 'styled-components';
import { } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1604335399105-a0c585fd81a1?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

 

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const NumberedListContainer = styled.div`
  margin: 3rem 0;
`;

const NumberedItem = styled.div`
  display: flex;
  margin-bottom: 2.5rem;
`;

const NumberCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #0d9488;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  margin-right: 1.5rem;
  flex-shrink: 0;
`;

const NumberedContent = styled.div`
  h3 {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
    color: #0d9488;
  }
  
  p {
    color: #e0e0e0;
    line-height: 1.6;
  }
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const LinenRental: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const stepsData = [
    {
      number: 1,
      title: language === 'it' ? 'NOLEGGIO' : 'RENTAL',
      description: language === 'it' 
        ? 'Gli operatori di alloggi possono noleggiare la biancheria necessaria, compresi lenzuola, federe, asciugamani, teli da bagno e altri articoli. Il noleggio può essere personalizzato in base alle esigenze specifiche di ogni struttura.'
        : 'Accommodation operators can rent necessary linens including sheets, pillowcases, towels, bath sheets and other items. Rental can be customized based on the specific needs of each facility.'
    },
    {
      number: 2,
      title: language === 'it' ? 'LAVAGGIO PROFESSIONALE' : 'PROFESSIONAL WASHING',
      description: language === 'it' 
        ? 'Dopo l\'uso da parte degli ospiti, la biancheria noleggiata viene raccolta e portata presso la lavanderia professionale. In questo processo, la biancheria viene accuratamente pulita, sanificata e piegata secondo i più alti standard.'
        : 'After use by guests, the rented linen is collected and taken to a professional laundry service. In this process, linens are carefully cleaned, sanitized and folded to the highest standards.'
    },
    {
      number: 3,
      title: language === 'it' ? 'CONSEGNA PROGRAMMATA' : 'SCHEDULED DELIVERY',
      description: language === 'it' 
        ? 'Una volta lavata e preparata, la biancheria viene consegnata direttamente alla struttura ricettiva. La consegna può essere programmata in base alle esigenze dell\'alloggio, garantendo che la biancheria sia pronta prima dell\'arrivo di nuovi ospiti.'
        : 'Once washed and prepared, the linen is delivered directly to the accommodation facility. Delivery can be scheduled according to the needs of the accommodation, ensuring that the linen is ready before the arrival of new guests.'
    },
    {
      number: 4,
      title: language === 'it' ? 'QUALITÀ E FRESCHEZZA' : 'QUALITY AND FRESHNESS',
      description: language === 'it' 
        ? 'I servizi di lavaggio professionale garantiscono che la biancheria sia di alta qualità e fresca per ogni ospite. Questo aiuta a garantire un\'esperienza piacevole e confortevole per coloro che soggiornano nella proprietà.'
        : 'Professional washing services ensure that linens are high quality and fresh for every guest. This helps ensure a pleasant and comfortable experience for those staying at the property.'
    },
    {
      number: 5,
      title: language === 'it' ? 'RISPARMIO DI TEMPO E RISORSE' : 'SAVING TIME AND RESOURCES',
      description: language === 'it' 
        ? 'Il servizio di noleggio, lavaggio e consegna della biancheria elimina il carico di lavoro associato al lavaggio e alla gestione della biancheria per gli operatori di alloggi, permettendo loro di concentrarsi su altri aspetti della gestione dell\'alloggio.'
        : 'The linen rental, washing and delivery service eliminates the workload associated with washing and managing linen for accommodation operators, allowing them to focus on other aspects of accommodation management.'
    },
    {
      number: 6,
      title: language === 'it' ? 'PERSONALIZZAZIONE DELLE OFFERTE' : 'CUSTOMIZATION OF OFFERS',
      description: language === 'it' 
        ? 'Alcuni servizi possono offrire la possibilità di personalizzare i pacchetti di noleggio in base alle dimensioni e al tipo di alloggio, offrendo una soluzione su misura per ogni cliente.'
        : 'Some services may offer the possibility of customizing rental packages based on the size and type of accommodation, offering a tailor-made solution for each customer.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Noleggio Biancheria | J-O-H' : 'Linen Rental | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizio di noleggio, lavaggio e consegna biancheria per strutture ricettive, una soluzione efficiente per la gestione degli alloggi' 
            : 'Linen rental, washing and delivery service for accommodation facilities, an efficient solution for accommodation management'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'NOLEGGIO BIANCHERIA' : 'LINEN RENTAL'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'JOLS è il servizio di noleggio, lavaggio e consegna di biancheria per alloggi, un\'opzione conveniente e professionale per chi gestisce strutture ricettive, come case vacanze, appartamenti in affitto o strutture alberghiere. Questo tipo di servizio offre un modo efficiente per garantire che gli alloggi siano sempre riforniti di biancheria, asciugamani e altri elementi essenziali.'
              : 'JOLS is the rental, washing and delivery service of linen for accommodation, a convenient and professional option for those who manage accommodation facilities, such as holiday homes, rental apartments or hotel facilities. This type of service offers an efficient way to ensure that accommodations are always stocked with linens, towels and other essentials.'}
          </Description>
          
          <NumberedListContainer>
            {stepsData.map((step) => (
              <NumberedItem key={step.number}>
                <NumberCircle>{step.number}</NumberCircle>
                <NumberedContent>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </NumberedContent>
              </NumberedItem>
            ))}
          </NumberedListContainer>
          
          <Description>
            {language === 'it' 
              ? 'JOLS offre una soluzione pratica ed efficiente per garantire la fornitura costante di biancheria pulita e di qualità nelle strutture ricettive, migliorando l\'esperienza degli ospiti e semplificando la gestione operativa per gli operatori.'
              : 'JOLS offers a practical and efficient solution to ensure the constant supply of clean, quality linen in accommodation facilities, improving the guest experience and simplifying operational management for operators.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Richiedi un Preventivo' : 'Request a Quote'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default LinenRental; 